/**
 * client/src/lib/chunk-reload.ts
 *
 * Auto-recover from a stale lazy-chunk load after a redeploy — the last
 * black-screen-after-sign-in path the app-wide ErrorBoundary (App.tsx, #1837)
 * does not fully cover.
 *
 * The router is heavily code-split (client/src/app/lazy-pages.ts — 50+
 * `React.lazy` routes). When the host redeploys, asset hashes change and the
 * previous build's chunk files are removed. A tab still holding the OLD build
 * (e.g. sitting on the sign-in page) that then navigates to a lazy route — the
 * post-sign-in redirect to /security-centre is the reported case — requests a
 * chunk URL that now 404s, and the dynamic `import()` rejects.
 *
 * With the ErrorBoundary present, that rejection is caught and shown as a
 * recoverable card — but "Try again" re-renders the SAME failed import and
 * loops; only a full reload fetches the fresh index.html + new chunk map. On a
 * build with NO boundary the same rejection unmounts the whole tree to the bare
 * dark <body> — the reported black screen.
 *
 * This handler turns that into a bounded automatic reload.
 *
 * Loop safety (the fix runs precisely when a deploy is in flight, so the NEW
 * build might ALSO be momentarily unfetchable): recovery is capped by a
 * per-tab-session ATTEMPT COUNT in sessionStorage — which survives the reload,
 * unlike a module variable. After {@link MAX_ATTEMPTS} rapid reloads we STOP
 * and let the ErrorBoundary card be the terminal state, so a genuinely broken
 * deploy can never spin the tab. A failure that arrives long after the last one
 * ({@link ATTEMPT_RESET_MS}) starts a fresh burst, so a legitimately new deploy
 * in a long-lived tab still auto-recovers.
 *
 * Blast radius (accepted): a `vite:preloadError` triggers a full reload of the
 * current session. In this app every dynamic import is a route chunk, so a
 * preload failure means a navigation is broken — reload is the right response.
 * An app that later adds non-critical below-the-fold lazy imports should scope
 * this before relying on it. The attempt cap bounds the worst case regardless.
 */

const ATTEMPT_KEY = "strenn:chunk-reload";
/** Hard cap on rapid auto-reloads within one failure burst — a broken deploy
 *  can trigger at most this many reloads before we fall back to the boundary. */
const MAX_ATTEMPTS = 2;
/** A failure this long after the previous one is treated as a NEW burst (fresh
 *  budget), so a real later deploy in a long-lived tab still recovers. */
const ATTEMPT_RESET_MS = 60_000;

interface AttemptState {
  /** Reloads fired in the current burst. */ n: number;
  /** Epoch ms of the last reload. */ at: number;
}

function readAttempts(): AttemptState {
  try {
    const raw = sessionStorage.getItem(ATTEMPT_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<AttemptState>;
      if (typeof p.n === "number" && typeof p.at === "number") {
        return { n: p.n, at: p.at };
      }
    }
  } catch {
    // sessionStorage unavailable (some private-mode configs) or corrupt JSON —
    // treat as a fresh budget; losing the loop guard is safer than never
    // recovering, and the in-context guard still prevents a same-load double.
  }
  return { n: 0, at: 0 };
}

function writeAttempts(state: AttemptState): void {
  try {
    sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify(state));
  } catch {
    /* best-effort — a private-mode tab just forgoes the cross-reload cap */
  }
}

/** Message signatures browsers + Vite use for a failed dynamic import. */
export function isChunkLoadError(message: string | undefined): boolean {
  if (!message) return false;
  return (
    message.includes("Failed to fetch dynamically imported module") || // Chrome/Firefox
    message.includes("error loading dynamically imported module") ||    // Vite preload
    message.includes("Importing a module script failed") ||             // Safari
    message.includes("ChunkLoadError") ||
    /Loading chunk \S+ failed/.test(message)
  );
}

/**
 * Install the stale-chunk auto-reload handlers. Call ONCE at boot, before React
 * renders (wired from main.tsx). Returns a disposer that removes the listeners
 * (used by tests; the boot caller ignores it — the handler lives for the tab).
 */
export function installChunkReloadHandler(): () => void {
  if (typeof window === "undefined") return () => {};

  // Guards a SINGLE page load against firing reload() twice when both
  // `vite:preloadError` and `unhandledrejection` surface the same failure.
  // reload() schedules navigation but the current script keeps running, so
  // without this a second event in the same tick would double-count the
  // sessionStorage budget. Fresh per install() closure — reset on the reload.
  let reloadingThisContext = false;

  /** Reload unless the in-context guard or the cross-reload budget forbids it.
   *  Returns whether it triggered a reload. */
  const attempt = (now: number): boolean => {
    if (reloadingThisContext) return false;
    const prev = readAttempts();
    const n = now - prev.at < ATTEMPT_RESET_MS ? prev.n : 0; // continue burst or reset
    if (n >= MAX_ATTEMPTS) return false; // budget spent → let the boundary show
    reloadingThisContext = true;
    writeAttempts({ n: n + 1, at: now });
    window.location.reload();
    return true;
  };

  // Vite fires this CustomEvent when a modulepreload / dynamic import fails.
  const onPreloadError = (event: Event): void => {
    // Only suppress Vite's default (rethrow as uncaught) when we actually
    // reload. On a budget-exhausted failure we let Vite rethrow so the REAL
    // chunk error reaches the ErrorBoundary, rather than preventDefault →
    // import resolves to undefined → a confusing secondary `undefined.default`.
    if (attempt(Date.now())) event.preventDefault();
  };

  // Belt-and-suspenders for a dynamic import() that rejects WITHOUT going
  // through Vite's preload helper (other bundlers, direct import()). Only act on
  // chunk-load messages — never reload for an unrelated rejected promise.
  const onUnhandledRejection = (event: PromiseRejectionEvent): void => {
    const reason: unknown = event.reason;
    const message =
      typeof reason === "string"
        ? reason
        : (reason as { message?: string } | null)?.message;
    if (isChunkLoadError(message) && attempt(Date.now())) {
      event.preventDefault();
    }
  };

  window.addEventListener("vite:preloadError", onPreloadError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);

  return () => {
    window.removeEventListener("vite:preloadError", onPreloadError);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
  };
}
